// ============================================================
// SHARED COMPONENTS
// Reused across all admin pages.
// ============================================================

import React from 'react';
import styles from './Shared.module.css';

// ── STAT CARD ──
export function StatCard({ icon, iconBg, value, label, trend, trendDir }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statTop}>
        <div className={styles.statIcon} style={{ background: iconBg }}>{icon}</div>
        {trend && (
          <span className={`${styles.statTrend} ${trendDir === 'up' ? styles.trendUp : trendDir === 'down' ? styles.trendDown : styles.trendFlat}`}>
            {trend}
          </span>
        )}
      </div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

// ── PAGE HEADER ──
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className={styles.pageHeader}>
      <div>
        <h2 className={styles.pageHeaderTitle}>{title}</h2>
        {subtitle && <p className={styles.pageHeaderSub}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── FILTER BAR ──
export function FilterBar({ filters, active, onChange }) {
  return (
    <div className={styles.filterBar}>
      {filters.map(f => (
        <button
          key={f.value}
          className={`${styles.filterBtn} ${active === f.value ? styles.filterActive : ''}`}
          onClick={() => onChange(f.value)}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── DATA TABLE ──
export function DataTable({ columns, rows, onRowClick }) {
  const gridTemplate = columns.map(c => c.width || '1fr').join(' ');

  return (
    <div className={styles.table}>
      <div className={styles.tableHead} style={{ gridTemplateColumns: gridTemplate }}>
        {columns.map(c => <div key={c.key}>{c.label}</div>)}
      </div>
      {rows.length === 0 ? (
        <div className={styles.tableEmpty}>Δεν υπάρχουν δεδομένα</div>
      ) : (
        rows.map((row, i) => (
          <div
            key={row.id || i}
            className={`${styles.tableRow} ${onRowClick ? styles.clickable : ''}`}
            style={{ gridTemplateColumns: gridTemplate }}
            onClick={() => onRowClick?.(row)}
          >
            {columns.map(c => (
              <div key={c.key} className={styles.tableCell}>
                {c.render ? c.render(row) : row[c.key]}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ── AVATAR CELL ──
export function AvatarCell({ initials, name, sub, color = '#C0185A' }) {
  return (
    <div className={styles.avatarCell}>
      <div className={styles.avatar} style={{ background: color }}>{initials}</div>
      <div>
        <div className={styles.avatarName}>{name}</div>
        {sub && <div className={styles.avatarSub}>{sub}</div>}
      </div>
    </div>
  );
}

// ── ACTION BUTTONS ──
export function ActionBtn({ children, variant = 'default', onClick, disabled }) {
  const cls = {
    default:  styles.btnDefault,
    approve:  styles.btnApprove,
    reject:   styles.btnReject,
    publish:  styles.btnPublish,
    primary:  styles.btnPrimary,
    danger:   styles.btnDanger,
  }[variant] || styles.btnDefault;

  return (
    <button
      className={`${styles.actionBtn} ${cls}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

// ── MODAL ──
export function Modal({ open, onClose, title, children, size = 'md' }) {
  if (!open) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${styles[`modal-${size}`]}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
}

// ── FORM FIELD ──
export function FormField({ label, required, children, error }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      {children}
      {error && <div className={styles.formError}>{error}</div>}
    </div>
  );
}

export function Input({ ...props }) {
  return <input className={styles.input} {...props} />;
}

export function Select({ children, ...props }) {
  return <select className={styles.input} {...props}>{children}</select>;
}

export function Textarea({ ...props }) {
  return <textarea className={styles.textarea} {...props} />;
}

// ── BAR CHART ──
export function BarChart({ data, maxValue }) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  return (
    <div className={styles.barChart}>
      {data.map((item, i) => (
        <div key={i} className={styles.barRow}>
          <div className={styles.barLabel}>{item.label}</div>
          <div className={styles.barTrack}>
            <div
              className={styles.barFill}
              style={{ width: `${(item.value / max) * 100}%`, background: item.color || 'var(--crimson)' }}
            />
          </div>
          <div className={styles.barValue}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── DETAIL ROW ──
export function DetailRow({ label, value, valueStyle }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailKey}>{label}</span>
      <span className={styles.detailVal} style={valueStyle}>{value}</span>
    </div>
  );
}

// ── CARD ──
export function Card({ title, action, children }) {
  return (
    <div className={styles.card}>
      {(title || action) && (
        <div className={styles.cardHeader}>
          {title && <div className={styles.cardTitle}>{title}</div>}
          {action && <div className={styles.cardAction}>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── PERIOD PICKER ── @period-picker-v1
// Month + year selector for the period-scoped figures on the admin
// dashboard and analytics pages. Both endpoints accept ?year=&month=
// and default to the current month when they are absent, so this is
// purely additive.
export const EL_MONTH_NAMES = [
  'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
  'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος',
];

export function PeriodPicker({ year, month, onChange, minYear = 2026, allowFuture = false }) { /* @period-picker-future: roster requests are for future months */
  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth() + 1;
  const years = [];
  for (let y = thisYear + (allowFuture ? 1 : 0); y >= minYear; y--) years.push(y);

  // Don't offer months that haven't happened yet in the current year.
  const maxMonth = (allowFuture || year !== thisYear) ? 12 : thisMonth;

  const sel = {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'white',
    color: 'var(--slate)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const isCurrent = year === thisYear && month === thisMonth;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>Περίοδος:</span>
      <select style={sel} value={month} onChange={e => onChange(year, parseInt(e.target.value))}>
        {EL_MONTH_NAMES.map((m, i) => (
          <option key={i} value={i + 1} disabled={i + 1 > maxMonth}>{m}</option>
        ))}
      </select>
      <select
        style={sel}
        value={year}
        onChange={e => {
          const y = parseInt(e.target.value);
          const cap = (allowFuture || y !== thisYear) ? 12 : thisMonth;
          onChange(y, Math.min(month, cap));
        }}
      >
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      {!isCurrent && (
        <button
          onClick={() => onChange(thisYear, thisMonth)}
          style={{ ...sel, border: 'none', color: 'var(--crimson)', background: 'transparent' }}
        >
          Τρέχων μήνας
        </button>
      )}
    </div>
  );
}

// ── EMPTY STATE ──
export function EmptyState({ icon, title, subtitle }) {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>{icon}</div>
      <div className={styles.emptyTitle}>{title}</div>
      {subtitle && <div className={styles.emptySub}>{subtitle}</div>}
    </div>
  );
}
